import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { formatCurrency } from '../utils/pricingUtils';
import { V2PricingCard, PaymentMethodsSection } from './subscription/PricingComponents';
import { useSubscriptionModal } from './hooks/useSubscriptionModal';
import { tierLabel } from '../config/labels';

const SubscriptionModal = ({ isOpen, onClose, user, defaultType = 'monthly' }) => {
  const {
    isLoading, investmentCount, pricingCalculation, pricingCalculationNGN,
    availableCurrencies, selectedCurrency, setSelectedCurrency, isLoadingPricing,
    useV2Pricing, subscriptionType, setSubscriptionType, isNigerian, handleSubscribe,
  } = useSubscriptionModal(isOpen, user, defaultType);

  const isChangingPaymentCycle = defaultType === 'annual';

  const renderPricingContent = () => {
    if (isLoadingPricing) return <div className="flex justify-center items-center py-12"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /><span className="ml-3 text-slate-600">Loading pricing...</span></div>;
    if (useV2Pricing && pricingCalculation) {
      return (
        <div className="space-y-6">
          {availableCurrencies.length > 1 && (
            <div className="flex justify-center items-center space-x-4">
              <label className="text-sm font-medium text-slate-700">Select Currency:</label>
              {availableCurrencies.map((curr) => (<button key={curr.currency} onClick={() => setSelectedCurrency(curr.currency)} className={`px-4 py-2 rounded-lg border-2 transition-all ${selectedCurrency === curr.currency ? 'border-purple-600 bg-purple-50 text-purple-700 font-semibold' : 'border-gray-300 hover:border-purple-400'}`}>{curr.symbol} {curr.currency}</button>))}
            </div>
          )}
          <V2PricingCard pricingCalculation={pricingCalculation} subscriptionType={subscriptionType} investmentCount={investmentCount} formatCurrencyFn={formatCurrency} />
        </div>
      );
    }
    const displayTier = tierLabel(user?.tier) || 'Continuity System';
    return (
      <div className="border border-gray-300 rounded-lg overflow-hidden">
        <div className="grid grid-cols-4 bg-gray-100 border-b border-gray-300">{['Subscription', 'Access', 'Price', 'Price'].map((h, i) => <div key={h + i} className="p-4 font-semibold text-slate-900 border-r border-gray-300 last:border-r-0">{h}</div>)}</div>
        <div className="grid grid-cols-4 bg-white"><div className="p-4 border-r border-gray-300 flex items-center font-semibold text-slate-900">{displayTier}</div><div className="p-4 border-r border-gray-300 flex items-center text-slate-700">Full platform access</div><div className="p-4 border-r border-gray-300 flex items-center font-semibold text-slate-900">$25/month</div><div className="p-4 flex items-center font-semibold text-slate-900">$250/year</div></div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" hideClose={false}>
        <DialogHeader className="text-center pb-6 space-y-4">
          <DialogTitle className="text-4xl font-serif font-bold text-slate-900">{isChangingPaymentCycle ? 'Switch to Annual Payment' : 'Activate Your Access'}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              <p className="text-lg text-slate-700">{isChangingPaymentCycle ? 'Save 2 months by switching to annual billing' : 'Your invitation to MyInvestFolio has been accepted.'}</p>
              {!isChangingPaymentCycle && <p className="text-base text-slate-600">To activate your account, subscribe below:</p>}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="my-8">{renderPricingContent()}</div>
        {useV2Pricing && pricingCalculation && !isChangingPaymentCycle && (
          <div className="pt-8 pb-4">
            <div className="flex items-center justify-center gap-4 bg-slate-50 rounded-xl p-4">
              <button onClick={() => setSubscriptionType('monthly')} className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${subscriptionType === 'monthly' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}>Pay-As-You-Go</button>
              <button onClick={() => setSubscriptionType('annual')} className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all relative ${subscriptionType === 'annual' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-600 hover:text-slate-900'}`}>Annual<span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">Save 17%</span></button>
            </div>
          </div>
        )}
        <PaymentMethodsSection isNigerian={isNigerian} pricingCalculation={pricingCalculation} pricingCalculationNGN={pricingCalculationNGN} subscriptionType={subscriptionType} user={user} isLoading={isLoading} isLoadingPricing={isLoadingPricing} handleSubscribe={handleSubscribe} />
        <p className="text-center text-xs text-slate-500 mt-6">Secure payment powered by {isNigerian ? 'PayStack & Stripe' : 'Stripe'}. Cancel anytime.</p>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionModal;
